import { isAxiosError } from 'axios';
import {
  readWapiState,
  type tPersistedPhase,
  type tPersistedWapiState,
} from 'src/hooks/whatsapp/wapiStateStorage';
import type { tEmbeddedSignupPath } from 'src/lib/facebook/embeddedSignupLogin';
import { resolveWapiActivation } from 'src/pages/WhatsappApi/hooks/useWapiNotConnected';
import { eIntegrationStatus } from 'src/providers/IntegrationsProvider/Integrations.enums';
import { legacyToMetaMap } from 'src/utils/ErrorDirectory';

/**
 * Pure state machine for the WhatsApp Business API connection flow, shared
 * between the Onboarding "Conecta WhatsApp" step and the Dashboard
 * Integrations modal. The reducer carries no side effects beyond reading
 * `Date.now()` for the `lastAttemptAt` timestamp; persistence happens in
 * `useWhatsAppConnectState` via an effect that mirrors state into
 * `wapiStateStorage`.
 *
 * Phase lifecycle:
 *   connect → connecting → (success | failed_error | failed_closed)
 *   connecting → verifying            (INT-1516: a held or superseded 200)
 *   connecting → activation_failed    (INT-1516: a failed 200)
 *   verifying → (success | activation_failed)   (the integration row decides)
 *   failed_* | verifying | activation_failed → connecting (via retry)
 *
 * INT-1516: a 200 from the connect endpoint is no longer success by itself.
 * Only an operational verdict is; a held record waits in `verifying` until the
 * integration row (bff-web) says CONNECTED or ACTIVATION_FAILED.
 */

export const META_RATE_LIMIT_CODES: readonly number[] = [133006, 133016];
export const SUCCESS_COUNTDOWN_SECONDS = 3;

export const isMetaRateLimitCode = (code: number | string | null | undefined): boolean => {
  const metaCode = typeof code === 'string' ? legacyToMetaMap[code] : code;
  return typeof metaCode === 'number' && META_RATE_LIMIT_CODES.includes(metaCode);
};

export type tPostConnectionPhase =
  | 'connect'
  | 'connecting'
  | 'verifying'
  | 'success'
  | 'failed_error'
  | 'failed_closed'
  | 'skipped'
  | 'escalated'
  /**
   * INT-1516 — Meta did not activate the number. Distinct from the two
   * `failed_*` phases, whose screens explain a popup failure from Meta's error
   * code and step; a verdict carries only a reason, and its remedy is always a
   * reconnect.
   */
  | 'activation_failed';

/**
 * INT-1516 — what the connect response (WBS `ConnectResponse`) says about the
 * record it created, per the wire contract (§4).
 */
export type tConnectVerdict = 'operational' | 'held' | 'failed' | 'superseded';

export type tConnectVerdictReading = {
  verdict: tConnectVerdict;
  /** WBS `activation_reason`, verbatim; null when absent or empty. */
  reason: string | null;
};

/**
 * INT-1516 — what the WhatsApp API integration row (bff-web) says about the
 * live record. `absent` covers every state without a live account, and any
 * state this build does not know.
 */
export type tObservedActivation = 'operational' | 'held' | 'failed' | 'absent';

/**
 * Maps the connect response to a verdict. Absent (off and shadow) or
 * `operational` is operational; `activation_failed` is failed; `superseded`
 * means a concurrent reconnect retired the record first; anything else,
 * `activating` and values this build does not know included, is held. An
 * unrecognized value is never read as connected.
 *
 * `null` counts as absent: the contract says off and shadow omit the key, and
 * reading a stray null as held would hold every healthy connect.
 */
export function readConnectVerdict(body: unknown): tConnectVerdictReading {
  const record =
    body !== null && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const state = record.activation_state;
  const rawReason = record.activation_reason;
  const reason = typeof rawReason === 'string' && rawReason.trim() !== '' ? rawReason : null;

  if (state === undefined || state === null || state === 'operational') {
    return { verdict: 'operational', reason: null };
  }
  if (state === 'activation_failed') return { verdict: 'failed', reason };
  if (state === 'superseded') return { verdict: 'superseded', reason: null };
  return { verdict: 'held', reason };
}

/** Rejects the code exchange that outlasts the connection hook's wait limit. */
export class WapiExchangeTimeoutError extends Error {
  constructor(timeoutMilliseconds: number) {
    super(`authenticateWhatsappAPI timed out after ${timeoutMilliseconds}ms`);
    this.name = 'WapiExchangeTimeoutError';
  }
}

/** What a failed connection event can say about the request behind it. */
export type tWapiFailureDetails = {
  http_status?: number;
  timed_out?: true;
};

/**
 * Reads the request facts a connection error event carries: the HTTP status
 * when the backend answered, or `timed_out` when the request ran out of time,
 * either in axios or against the connection hook's own limit. Any other
 * failure carries neither, so a prop is only ever sent where it is real.
 *
 * @param {unknown} error - The failure passed to `onError`.
 * @returns {tWapiFailureDetails} The props to spread into the event.
 */
export function readWapiFailureDetails(error: unknown): tWapiFailureDetails {
  if (error instanceof WapiExchangeTimeoutError) return { timed_out: true };
  if (!isAxiosError(error)) return {};
  if (error.response) return { http_status: error.response.status };
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT')
    return { timed_out: true };
  return {};
}

/**
 * Maps the WhatsApp API integration state to what it says about activation.
 * Held and failed come from `resolveWapiActivation`, the one resolver every
 * surface uses, so a state added there reaches the dialog too.
 */
export function toObservedActivation(state: string | undefined): tObservedActivation {
  if (state === eIntegrationStatus.CONNECTED) return 'operational';
  const activation = resolveWapiActivation(state);
  if (activation === 'pending') return 'held';
  if (activation === 'failed') return 'failed';
  return 'absent';
}

/**
 * INT-1217 — the diagnostic half of Meta's `CANCEL`, delivered only when the
 * customer presses «Reportar error a Leadsales» inside the popup. Meta's
 * `error_message` already arrives in Spanish, so the failure screen shows it
 * verbatim. `session_id` is deliberately absent: it is a Support-side
 * identifier, already persisted by INT-1216, and never shown to the customer.
 */
export type tEmbeddedSignupErrorReport = {
  code: number | null;
  message: string | null;
};

export type tConnectionState = {
  phase: tPostConnectionPhase;
  error: string | null;
  /**
   * Stable backend error code from the connect endpoint's error envelope
   * (e.g. wba-10073 "no WABA in token", INT-914) — lets the failure screen
   * render actionable copy for known causes. Persisted to wapiStateStorage
   * with a failed attempt, so a failure screen restored after a reload or a
   * remount still names its cause.
   */
  errorCode: string | null;
  /**
   * INT-1217 — raw `current_step` from Meta's abandonment `CANCEL`, selecting
   * step-specific guidance on the failure screens. Raw string, never an enum:
   * Meta ships steps it has not documented (`WHATSAPP_BUSINESS_APP_ONBOARDING_
   * PHONE_CREATION` is one we see in production and Meta does not list).
   *
   * Follows `errorCode`'s rule: persisted with a failed attempt, so a restored
   * failure screen keeps the guidance for the step where it happened.
   */
  signupStep: string | null;
  /** See `tEmbeddedSignupErrorReport`. Persisted, same rule. */
  signupErrorReport: tEmbeddedSignupErrorReport | null;
  /**
   * INT-1516 — why the record is held (`verifying`) or failed
   * (`activation_failed`), e.g. `cloud_api_account_missing`: WBS
   * `activation_reason` from the connect response, or bff-web's `reason` on the
   * integration row. An open set, passed through verbatim; the failure screen
   * picks copy for the reasons it knows and generic copy for the rest. Not
   * persisted: after a reload the integration row supplies it again.
   */
  activationReason: string | null;
  /**
   * True once Meta's window answered with a code and Leadsales is exchanging
   * it, while the phase stays `connecting`. Lets a surface tell "Meta's window
   * is open" apart from "Meta finished, the number is being activated" without
   * a new phase, which every consumer's phase switch would have to handle.
   * Cleared whenever the attempt leaves `connecting`; never persisted.
   */
  isExchangingCode: boolean;
  countdown: number;
  retryCount: number;
  hasSkippedBefore: boolean;
  lastAttemptAt: number;
  /**
   * The Embedded Signup flow the latest attempt asked for, persisted with it so
   * a retry after a reload opens the same flow. Absent when no attempt chose
   * one, including an attempt launched with the builder's default.
   */
  path?: tEmbeddedSignupPath;
  /**
   * True while the screen on show comes from the screen simulator, not from a
   * real attempt. Nothing is persisted or tracked for it; a real connection
   * start or a reset clears it.
   */
  isSimulated: boolean;
};

export type tConnectionAction =
  | { type: 'START_CONNECTING'; path?: tEmbeddedSignupPath }
  /** Meta's window answered with a code; see `isExchangingCode`. */
  | { type: 'META_RESPONDED' }
  | { type: 'SUCCESS' }
  | { type: 'FAIL_ERROR'; error: string; errorCode?: string }
  | { type: 'FAIL_CLOSED' }
  | { type: 'SKIP' }
  | { type: 'TICK' }
  | { type: 'RESET' }
  /** The screen simulator produced the current state; see `isSimulated`. */
  | { type: 'MARK_SIMULATED' }
  /**
   * Replaces a simulated state with the real one, rebuilt from storage, which
   * a simulation never writes. Ignored when the state is not simulated.
   */
  | { type: 'LEAVE_SIMULATION'; realState: tConnectionState }
  | {
      type: 'BACK_TO_CONNECT';
    }
  | {
      /**
       * INT-1217 — records what Meta reported about the popup: the abandonment
       * step, and/or the customer-consented error report.
       *
       * Accepted in EVERY phase and never touches `phase`, `retryCount` or
       * `lastAttemptAt`. That matters because Meta's `postMessage` arrives while
       * the attempt is still `connecting` — before `FAIL_CLOSED` — so an action
       * gated on a phase (as both FAIL_* actions are) would have nothing to
       * attach to, and an action that wrote `phase` would corrupt a live
       * attempt.
       *
       * Fields merge rather than replace: an abandonment carries only a step and
       * an error report carries only code/message, so a customer who does both
       * produces two records that must both survive.
       */
      type: 'EMBEDDED_SIGNUP_REPORTED';
      step: string | null;
      errorReport: tEmbeddedSignupErrorReport | null;
    }
  | {
      /**
       * INT-1516 — the connect response held the record (or, `superseded`,
       * cannot say what is live). The response arrives while the attempt is
       * `connecting`. `connect` stays accepted for the pre-existing
       * out-of-band use.
       */
      type: 'VERIFY';
      reason?: string | null;
    }
  | {
      /** INT-1516 — the connect response said Meta did not activate the record. */
      type: 'FAIL_ACTIVATION';
      reason: string | null;
    }
  | {
      /**
       * INT-1516 — what the integration row says, dispatched whenever the row
       * changes (and on mount). It ends a hold, shows a failure found on
       * refetch, and restores the verdict phases after a reload. The
       * connecting attempt's own response decides, so this is ignored while
       * `connecting`; `success` is terminal.
       */
      type: 'ACTIVATION_OBSERVED';
      activation: tObservedActivation;
      reason: string | null;
    }
  | ({
      type: 'RESYNC_FROM_STORAGE';
      retryCount: number;
      phase: tPersistedPhase;
      lastAttemptAt: number;
    } & Pick<
      tPersistedWapiState,
      'errorCode' | 'signupStep' | 'signupErrorReport' | 'path'
    >);

export const RESTORABLE_PHASES = [
  'connect',
  'failed_error',
  'failed_closed',
  'skipped',
] as const satisfies readonly tPostConnectionPhase[];

/**
 * The Meta step the screen simulator's close scenes report: the one Meta
 * reports most. Here, not in the hook, so a consumer that mocks the hook can
 * still read it.
 */
export const WAPI_DEV_SCENARIO_CLOSED_STEP = 'ASSET_SELECTION';

function enterSuccess(state: tConnectionState): tConnectionState {
  return {
    ...state,
    phase: 'success',
    countdown: SUCCESS_COUNTDOWN_SECONDS,
    error: null,
    errorCode: null,
    signupStep: null,
    signupErrorReport: null,
    activationReason: null,
    isExchangingCode: false,
    // Reset the retry counter so a re-connection later starts fresh.
    retryCount: 0,
  };
}

// INT-1516: a verdict is not a popup failure, so neither verdict phase counts a
// retry or stamps `lastAttemptAt`.
function enterVerifying(state: tConnectionState, reason: string | null): tConnectionState {
  return {
    ...state,
    phase: 'verifying',
    error: null,
    errorCode: null,
    activationReason: reason,
    isExchangingCode: false,
  };
}

function enterActivationFailed(
  state: tConnectionState,
  reason: string | null,
): tConnectionState {
  return {
    ...state,
    phase: 'activation_failed',
    error: null,
    errorCode: null,
    activationReason: reason,
    isExchangingCode: false,
  };
}

/**
 * INT-1516 — the integration row's verdict, applied by phase.
 *
 * Only a positive verdict (operational or failed) ends a hold. A row without a
 * live account can be a read that has not caught up with the connect (a
 * replica, a fetch that started before the commit), so it never sends a held
 * dialog back to the connect screen. For the same reason the hook dispatches
 * this only when the row CHANGES: right after the 200, the unchanged row still
 * describes the record the connect replaced.
 */
type tActivationObservedAction = Extract<tConnectionAction, { type: 'ACTIVATION_OBSERVED' }>;

function observeActivation(
  state: tConnectionState,
  observed: tActivationObservedAction,
): tConnectionState {
  const { activation, reason } = observed;
  switch (state.phase) {
    case 'connecting':
    case 'success':
      return state;

    case 'verifying':
      if (activation === 'operational') return enterSuccess(state);
      if (activation === 'failed') return enterActivationFailed(state, reason);
      return state;

    case 'activation_failed':
      // A failed record can still heal on traffic, and a reconnect made
      // elsewhere (another tab, the banner) can be held again.
      if (activation === 'operational') return enterSuccess(state);
      if (activation === 'held') return enterVerifying(state, reason);
      return state;

    default:
      // An idle dialog (connect, failed_*, skipped, escalated) shows what the
      // row says: a reload, a reopened dialog, another tab's connect. An
      // operational row is left to `autoSuccessOnAlreadyConnected`, so the
      // Dashboard dialog opened over a connected account does not auto-close.
      if (activation === 'held') return enterVerifying(state, reason);
      if (activation === 'failed') return enterActivationFailed(state, reason);
      return state;
  }
}

export function connectionReducer(
  state: tConnectionState,
  action: tConnectionAction,
): tConnectionState {
  switch (action.type) {
    case 'START_CONNECTING':
      if (
        state.phase !== 'connect' &&
        state.phase !== 'failed_error' &&
        state.phase !== 'failed_closed' &&
        state.phase !== 'skipped' &&
        // INT-1516: reconnect from the held screen (secondary action) and from
        // the activation failure (primary action).
        state.phase !== 'verifying' &&
        state.phase !== 'activation_failed'
      )
        return state;
      return {
        ...state,
        phase: 'connecting',
        error: null,
        errorCode: null,
        // INT-1217: a step captured in the previous attempt must not label this
        // one. Mirrors the `sessionInfoRef` reset-per-attempt rule.
        signupStep: null,
        signupErrorReport: null,
        activationReason: null,
        isExchangingCode: false,
        path: action.path,
        isSimulated: false,
      };

    case 'META_RESPONDED':
      if (state.phase !== 'connecting') return state;
      return { ...state, isExchangingCode: true };

    case 'VERIFY':
      // INT-1516: the connect response held the record. It arrives while the
      // attempt is `connecting`; this transition was unreachable before, since
      // the guard accepted only `connect` and nothing dispatched it.
      if (state.phase !== 'connect' && state.phase !== 'connecting') return state;
      return enterVerifying(state, action.reason ?? null);

    case 'FAIL_ACTIVATION':
      if (state.phase !== 'connecting') return state;
      return enterActivationFailed(state, action.reason);

    case 'ACTIVATION_OBSERVED':
      return observeActivation(state, action);

    case 'SUCCESS':
      if (
        state.phase !== 'connecting' &&
        state.phase !== 'verifying' &&
        state.phase !== 'connect'
      )
        return state;
      return enterSuccess(state);

    case 'FAIL_ERROR': {
      if (state.phase !== 'connecting') return state;
      const nextRetryCount = state.retryCount + 1;
      return {
        ...state,
        phase: isMetaRateLimitCode(action.errorCode) || isMetaRateLimitCode(state.signupErrorReport?.code)
          ? 'escalated'
          : 'failed_error',
        error: action.error,
        errorCode: action.errorCode ?? null,
        isExchangingCode: false,
        retryCount: nextRetryCount,
        lastAttemptAt: Date.now(),
      };
    }

    case 'FAIL_CLOSED': {
      if (state.phase !== 'connecting') return state;
      const nextRetryCount = state.retryCount + 1;
      return {
        ...state,
        phase: isMetaRateLimitCode(state.signupErrorReport?.code) ? 'escalated' : 'failed_closed',
        isExchangingCode: false,
        retryCount: nextRetryCount,
        lastAttemptAt: Date.now(),
      };
    }

    case 'EMBEDDED_SIGNUP_REPORTED':
      // Phase-independent on purpose — see the action's docblock. Merges so an
      // abandonment (step only) and an error report (code/message only) for the
      // same attempt both survive, in whichever order they arrive.
      return {
        ...state,
        signupStep: action.step ?? state.signupStep,
        signupErrorReport: action.errorReport ?? state.signupErrorReport,
      };

    case 'SKIP':
      if (state.phase !== 'connect') return state;
      return { ...state, phase: 'skipped', hasSkippedBefore: true };

    case 'TICK':
      if (state.phase !== 'success') return state;
      // Stay-stable when the countdown is already drained; otherwise a stale
      // setInterval keeps re-rendering the modal at 1 Hz after the consumer's
      // onSuccess has already navigated away or closed the surface.
      if (state.countdown === 0) return state;
      return { ...state, countdown: Math.max(0, state.countdown - 1) };

    case 'RESET':
      return {
        phase: 'connect',
        error: null,
        errorCode: null,
        signupStep: null,
        signupErrorReport: null,
        activationReason: null,
        isExchangingCode: false,
        countdown: 0,
        retryCount: 0,
        hasSkippedBefore: state.hasSkippedBefore,
        lastAttemptAt: 0,
        path: undefined,
        isSimulated: false,
      };

    case 'MARK_SIMULATED':
      return state.isSimulated ? state : { ...state, isSimulated: true };

    case 'LEAVE_SIMULATION':
      if (!state.isSimulated) return state;
      return { ...action.realState, isSimulated: false };

    case 'BACK_TO_CONNECT':
      if (
        state.phase !== 'failed_error' &&
        state.phase !== 'failed_closed' &&
        state.phase !== 'escalated' &&
        state.phase !== 'activation_failed'
      ) {
        return state;
      }
      return {
        ...state,
        phase: 'connect',
        error: null,
        errorCode: null,
        signupStep: null,
        signupErrorReport: null,
        activationReason: null,
      };

    case 'RESYNC_FROM_STORAGE': {
      // INT-1516: activation_failed comes from the integration row, so a peer
      // tab's popup failure must not replace it.
      if (
        state.phase === 'connecting' ||
        state.phase === 'verifying' ||
        state.phase === 'success' ||
        state.phase === 'activation_failed'
      ) {
        return state;
      }
      if (action.retryCount <= state.retryCount) return state;
      return {
        ...state,
        phase: action.phase,
        retryCount: action.retryCount,
        lastAttemptAt: action.lastAttemptAt,
        // Taken with the phase, or this tab's persist effect would write its own
        // empty cause over the one the peer tab stored.
        errorCode: action.errorCode ?? null,
        signupStep: action.signupStep ?? null,
        signupErrorReport: action.signupErrorReport ?? null,
        path: action.path,
      };
    }

    default:
      return state;
  }
}

function freshState(): tConnectionState {
  return {
    phase: 'connect',
    error: null,
    errorCode: null,
    signupStep: null,
    signupErrorReport: null,
    activationReason: null,
    isExchangingCode: false,
    countdown: 0,
    retryCount: 0,
    hasSkippedBefore: false,
    lastAttemptAt: 0,
    isSimulated: false,
  };
}

export function createInitialState(workspaceId: string | undefined): tConnectionState {
  if (!workspaceId) return freshState();

  let persisted: tPersistedWapiState | null = null;
  try {
    persisted = readWapiState(workspaceId);
  } catch {
    /* defensive: storage access can throw in some browsers */
  }

  if (!persisted) return freshState();
  if (!(RESTORABLE_PHASES as readonly string[]).includes(persisted.phase)) return freshState();

  return {
    phase: persisted.phase,
    error: null,
    // The cause is stored only with a failed attempt, so a restored failure
    // screen names it and any other restored phase has none.
    errorCode: persisted.errorCode ?? null,
    signupStep: persisted.signupStep ?? null,
    signupErrorReport: persisted.signupErrorReport ?? null,
    // INT-1516: the verdict phases are never persisted; the integration row
    // restores them after a reload.
    activationReason: null,
    isExchangingCode: false,
    countdown: 0,
    retryCount: persisted.retryCount,
    hasSkippedBefore: persisted.hasSkippedBefore,
    lastAttemptAt: persisted.lastAttemptAt,
    path: persisted.path,
    isSimulated: false,
  };
}
